import { medicationOrderRefusalMessage } from './medications/order-write-contract';
/**
 * Standardised toast voice for Statura Care.
 *
 * Voice guidelines:
 *   - Success: past tense + verb. "Saved", "Submitted", "Closed incident".
 *   - Failure: "Couldn't {verb} {object}" — never raw error strings.
 *   - Info: neutral. "4 items updated", "Export ready".
 *   - Undo: short. "Incident closed" + Undo button.
 *
 * DO NOT pass raw `err.message` to any of these — the helpers handle the
 * user-facing copy. Raw errors leak implementation detail and sometimes PHI.
 *
 * Sentry logging for failures is handled inside `toastFail` — callers do not
 * need to log separately.
 */

import { toast } from 'sonner';
import { captureClientOperationalError } from './sentry-client-observability';
import { invoiceCreateFailure } from './finance/invoice-create-errors';
import { incidentPriorityRefusal } from './incidents/priority-refusals';
import { isUserSafeReason } from '@/lib/user-safe-reason';

/** Fixed copy for the assessment-scope refusals the database raises by token. */
const ASSESSMENT_SCOPE_COPY: Readonly<Record<string, string>> = {
  assessment_scope_capture_identity_mismatch:
    'You can only start assessments you are completing yourself.',
  assessment_scope_active_actor_staff_required:
    'Your account needs an active staff record before you can start an assessment.',
  assessment_scope_client_and_assessor_required:
    'Select an eligible assessor before starting the assessment.',
  assessment_scope_configuration_blocked:
    'Your current clinical scope is not eligible to start this assessment. Check with your clinical lead.',
  assessment_scope_named_rn_delegate_required:
    'Select an eligible registered nurse accountable for sign-off.',
  assessment_scope_named_rn_configuration_blocked:
    'The selected registered nurse is not eligible to sign off this assessment.',
};

/** Match known tokens only (optionally followed by `:details`); never other text. */
function assessmentScopeCopy(value: unknown): string | null {
  const record =
    typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
  const candidates = record ? [record.code, record.message, record.error] : [value];
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const text = candidate.trim();
    const colon = text.indexOf(':');
    const token = colon === -1 ? text : text.slice(0, colon);
    if (Object.hasOwn(ASSESSMENT_SCOPE_COPY, token)) return ASSESSMENT_SCOPE_COPY[token] ?? null;
  }
  return null;
}

type ServerActionFailure = Readonly<{ success: false; error: unknown }>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isServerActionFailure(value: unknown): value is ServerActionFailure {
  return isRecord(value) && value.success === false && 'error' in value;
}

/**
 * Returns copy that has been explicitly classified as safe for a user-facing
 * toast. Infrastructure text and unmapped machine tokens are rejected, even when wrapped
 * in a server-action result.
 */
export function toastFailReason(error: unknown): string | null {
  const priorityRefusal = incidentPriorityRefusal(error);
  if (priorityRefusal) return priorityRefusal.message;

  const medicationRefusal = medicationOrderRefusalMessage(error);
  if (medicationRefusal) return medicationRefusal;
  const controlled = invoiceCreateFailure(error);
  if (controlled) return controlled.error;
  const assessmentScope = assessmentScopeCopy(error);
  if (assessmentScope) return assessmentScope;

  if (!isServerActionFailure(error) || typeof error.error !== 'string') return null;
  const message = error.error.trim();
  return message && isUserSafeReason(message) ? message : null;
}

/** Free text (including the action label) may contain personal or clinical data. */
function captureToastFailure(error: unknown) {
  const code = invoiceCreateFailure(error)?.code;
  captureClientOperationalError(
    new Error(code ? `Client action failed (${code})` : 'Client action failed'),
    { source: 'react_mutation_error', operation: 'toast.failure', status: code ?? 'unknown' },
  );
}

/**
 * Success toast — past tense. e.g., "Incident saved", "Claim submitted".
 */
export function toastOk(message: string) {
  toast.success(message);
}

/**
 * Failure toast — presents generic copy and captures sanitised operational metadata.
 *
 * @param action Verb phrase describing what was attempted. Used only in the
 *   toast copy ("Couldn't save incident"). Never exported to telemetry.
 * @param err Optional underlying error. Only allow-listed codes reach telemetry.
 */
export function toastFail(action: string, err?: unknown) {
  const msg = `Couldn't ${action}. Please try again.`;
  toast.error(msg);
  captureToastFailure(err);
}

/**
 * Failure toast that can show a server-action reason only after it passes the
 * controlled user-safety classifier. Unknown failures retain the standard
 * generic copy. Both paths capture once without exporting raw text or action labels.
 */
export function toastFailWithReason(action: string, err: unknown) {
  const reason = toastFailReason(err);
  if (reason) {
    toast.error(reason);
    captureToastFailure(err);
    return;
  }
  toastFail(action, err);
}

/**
 * Neutral info toast — for non-success, non-error notifications.
 */
export function toastInfo(message: string) {
  toast(message);
}

/**
 * Warning toast — use when the action succeeded but with caveats.
 * e.g., "Saved, but 2 fields need review".
 */
export function toastWarn(message: string) {
  toast.warning(message);
}

interface UndoOptions {
  /** Seconds the undo window stays open. Default 5. */
  durationSec?: number;
  /** Label on the undo button. Default "Undo". */
  label?: string;
}

/**
 * Success toast with an Undo action button. Use for reversible destructive
 * actions (close incident, complete task, archive client).
 *
 * @param message Past-tense confirmation. e.g., "Incident closed".
 * @param onUndo Callback invoked if user clicks Undo within the window.
 */
export function toastUndo(
  message: string,
  onUndo: () => void | Promise<void>,
  opts: UndoOptions = {},
) {
  const durationMs = (opts.durationSec ?? 5) * 1000;
  toast.success(message, {
    duration: durationMs,
    action: {
      label: opts.label ?? 'Undo',
      onClick: () => {
        void onUndo();
      },
    },
  });
}
